import { create } from 'zustand';

interface IdentityFlowDialogActions {
    opened: (callback?: () => void) => void;
    documentAttemptStarted: () => void;
    documentAttemptFailed: () => void;
    completed: () => void;
    dismissed: () => void;
}

interface IdentityFlowDialogState {
    isOpen: boolean;
    callback?: () => void;
    // True once the user starts a NEW document check inside this dialog. Until then the
    // registration store still holds the result of whatever check the user last completed
    // (usually `approved`, from signup), which says nothing about the step-up this dialog
    // was opened for. Readers use this flag to ignore that stale result — see
    // `useIdentityFlowDocument`.
    documentAttempted: boolean;
    actions: IdentityFlowDialogActions;
}

export const useIdentityFlowDialogStore = create<IdentityFlowDialogState>()((set) => ({
    isOpen: false,
    documentAttempted: false,
    actions: {
        opened: (callback?: () => void) => set({ isOpen: true, callback, documentAttempted: false }),
        // The user started a new document check, so the registration store's document state
        // now describes THIS attempt and can drive the dialog again.
        documentAttemptStarted: () => set({ documentAttempted: true }),
        // The attempt never got off the ground (e.g. the docv token request failed), so the
        // document state is stale again and must go back to being ignored.
        documentAttemptFailed: () => set({ documentAttempted: false }),
        // Identity verification succeeded — run the resume callback (which restarts the
        // deposit/withdrawal flow), then close.
        completed: () => {
            set((state) => {
                state.callback?.();
                return { isOpen: false, callback: undefined, documentAttempted: false };
            });
        },
        // User cancelled/dismissed the flow (X, background click) — close WITHOUT running
        // the resume callback, so we don't restart and auto-initiate the deposit they abandoned.
        dismissed: () => set({ isOpen: false, callback: undefined, documentAttempted: false }),
    },
}));
